import { ReactElement, useEffect, useRef } from 'react';

import { BootstrapLoading, COLOR_TOKENS } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

import { Box, Button, Typography } from '@mui/material';
import { SxProps, Theme } from '@mui/system';

import { useBootstrap } from '../hooks/useBootstrap';
import { usePostHogAnalytics } from '../hooks/usePostHogAnalytics';
import { useAppSelector } from '../redux/hooks';
import { getSandboxVisitorId, IS_SANDBOX } from '../utils/sandboxSession';

const STRINGS = appStrings.modules.sandboxUnavailable;

type AuthShellProps = Readonly<{
  children: ReactElement;
}>;

export const AuthShell = ({ children }: AuthShellProps) => {
  const { isBootstrapped, isSandboxUnavailable } = useBootstrap();
  const posthog = usePostHogAnalytics();
  const user = useAppSelector((state) => state.user.user);
  const previousUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!posthog) return;

    if (user?.id) {
      const backrsId = user.id.toString();
      // A sandbox visitor gets a new account every night, so identifying by it would split one
      // prospect across days.
      const distinctId = IS_SANDBOX ? getSandboxVisitorId() : backrsId;

      if (previousUserIdRef.current === distinctId) return;

      // Keep separate: identify(id, props) silently drops props on first-time
      // anon→backrs_id transitions. Do not collapse back into one call.
      posthog.identify(distinctId);
      posthog.setPersonProperties({
        backrs_id: backrsId,
        roles: user.roles?.map((role) => role.name) ?? [],
      });
      previousUserIdRef.current = distinctId;
    } else if (previousUserIdRef.current) {
      posthog.reset();
      previousUserIdRef.current = null;
    }
  }, [user?.id, user?.roles, posthog]);

  if (!isBootstrapped) {
    return <BootstrapLoading />;
  }

  // Without a session there is no signed-out experience to fall back to, so say so rather than
  // rendering an empty shell.
  if (isSandboxUnavailable) {
    return (
      <Box sx={unavailableStyle}>
        <Typography variant="text-xl-semibold">{STRINGS.title}</Typography>
        <Typography variant="text-sm-regular" sx={unavailableSubtitleStyle}>
          {STRINGS.subtitle}
        </Typography>
        <Button
          id="sandbox-unavailable-retry-button"
          hierarchy="Primary"
          size="md"
          onClick={() => window.location.reload()}
        >
          {STRINGS.cta}
        </Button>
      </Box>
    );
  }

  return children;
};

const unavailableStyle: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  textAlign: 'center',
  gap: 2,
  px: 3,
  height: '100dvh',
  backgroundColor: COLOR_TOKENS['Beige/25'],
};

const unavailableSubtitleStyle: SxProps<Theme> = {
  maxWidth: '32rem',
  color: COLOR_TOKENS['Gray/600'],
};
